import React, { useState } from 'react';
import { View, Text, Pressable, Modal, ScrollView, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { FlagType, FLAG_CONFIG } from '../../shared/types/due-diligence';
import { FlagBadge } from './FlagBadge';
import { Ionicons } from '@expo/vector-icons';

export interface FlagSelectorProps {
  value: FlagType | null | undefined;
  onSelect: (flag: FlagType | null) => void;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'badge';
}

const FLAG_OPTIONS: (FlagType | null)[] = [
  'big_red',
  'red',
  'yellow',
  'green',
  'big_green',
  'clear',
  'pending',
  'not_applicable',
  null, // Clear selection
];

export function FlagSelector({ 
  value, 
  onSelect, 
  size = 'md',
  variant = 'button'
}: FlagSelectorProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile } = useResponsive();
  const [modalVisible, setModalVisible] = useState(false);

  const handleSelect = (flag: FlagType | null) => {
    onSelect(flag);
    setModalVisible(false);
  };

  const selectorStyles = makeSelectorStyles(tokens, utils, isMobile);

  if (variant === 'badge') {
    return (
      <>
        <Pressable
          onPress={() => setModalVisible(true)}
          style={({ pressed }) => [
            selectorStyles.badgeButton,
            pressed && selectorStyles.pressed,
          ]}
        >
          {value ? (
            <FlagBadge flag={value} size={size} />
          ) : (
            <View style={selectorStyles.placeholderBadge}>
              <Text style={selectorStyles.placeholderText}>
                Set Flag
              </Text>
            </View>
          )}
        </Pressable>

        <Modal
          visible={modalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setModalVisible(false)}
        >
          <Pressable
            style={selectorStyles.modalOverlay}
            onPress={() => setModalVisible(false)}
          >
            <Pressable
              style={selectorStyles.modalContent}
              onPress={(e) => e.stopPropagation()}
            >
              <View style={selectorStyles.modalHeader}>
                <Text style={selectorStyles.modalTitle}>Select Flag</Text>
                <Pressable
                  onPress={() => setModalVisible(false)}
                  style={selectorStyles.closeButton}
                >
                  <Ionicons name="close" size={24} color={tokens.color.onSurface} />
                </Pressable>
              </View>

              <ScrollView style={selectorStyles.optionsList}>
                {FLAG_OPTIONS.map((flag) => {
                  const isSelected = flag === value;
                  const config = flag ? FLAG_CONFIG[flag] : { label: 'None', icon: 'close-outline', color: tokens.color.onSurface };

                  return (
                    <Pressable
                      key={flag || 'none'}
                      onPress={() => handleSelect(flag)}
                      style={({ pressed }) => [
                        selectorStyles.option,
                        isSelected && selectorStyles.optionSelected,
                        pressed && selectorStyles.optionPressed,
                      ]}
                    >
                      <View style={selectorStyles.optionContent}>
                        <View style={selectorStyles.optionIconContainer}>
                          <Ionicons 
                            name={config.icon as any} 
                            size={18} 
                            color={config.color} 
                          />
                          {flag && FLAG_CONFIG[flag]?.iconDouble && (
                            <Ionicons 
                              name={config.icon as any} 
                              size={18} 
                              color={config.color}
                              style={{ marginLeft: -6 }}
                            />
                          )}
                        </View>
                        <Text style={[
                          selectorStyles.optionLabel,
                          isSelected && selectorStyles.optionLabelSelected,
                        ]}>
                          {config.label}
                        </Text>
                      </View>
                      {isSelected && (
                        <Ionicons 
                          name="checkmark" 
                          size={20} 
                          color={tokens.color.primary} 
                        />
                      )}
                    </Pressable>
                  );
                })}
              </ScrollView>
            </Pressable>
          </Pressable>
        </Modal>
      </>
    );
  }

  // Button variant
  return (
    <>
      <Pressable
        onPress={() => setModalVisible(true)}
        style={({ pressed }) => [
          selectorStyles.button,
          pressed && selectorStyles.pressed,
        ]}
      >
        {value ? (
          <FlagBadge flag={value} size={size} />
        ) : (
          <>
            <Ionicons 
              name="flag-outline" 
              size={18} 
              color={tokens.color.onSurface} 
            />
            <Text style={selectorStyles.buttonText}>
              Set Flag
            </Text>
          </>
        )}
      </Pressable>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <Pressable
          style={selectorStyles.modalOverlay}
          onPress={() => setModalVisible(false)}
        >
          <Pressable
            style={selectorStyles.modalContent}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={selectorStyles.modalHeader}>
              <Text style={selectorStyles.modalTitle}>Select Flag</Text>
              <Pressable
                onPress={() => setModalVisible(false)}
                style={selectorStyles.closeButton}
              >
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>

            <ScrollView style={selectorStyles.optionsList}>
              {FLAG_OPTIONS.map((flag) => {
                const isSelected = flag === value;
                const config = flag ? FLAG_CONFIG[flag] : { label: 'None', icon: 'close-outline', color: tokens.color.onSurface };

                return (
                  <Pressable
                    key={flag || 'none'}
                    onPress={() => handleSelect(flag)}
                    style={({ pressed }) => [
                      selectorStyles.option,
                      isSelected && selectorStyles.optionSelected,
                      pressed && selectorStyles.optionPressed,
                    ]}
                  >
                    <View style={selectorStyles.optionContent}>
                      <View style={selectorStyles.optionIconContainer}>
                        <Ionicons 
                          name={config.icon as any} 
                          size={18} 
                          color={config.color} 
                        />
                        {flag && FLAG_CONFIG[flag]?.iconDouble && (
                          <Ionicons 
                            name={config.icon as any} 
                            size={18} 
                            color={config.color}
                            style={{ marginLeft: -6 }}
                          />
                        )}
                      </View>
                      <Text style={[
                        selectorStyles.optionLabel,
                        isSelected && selectorStyles.optionLabelSelected,
                      ]}>
                        {config.label}
                      </Text>
                    </View>
                    {isSelected && (
                      <Ionicons 
                        name="checkmark" 
                        size={20} 
                        color={tokens.color.primary} 
                      />
                    )}
                  </Pressable>
                );
              })}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function makeSelectorStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean
) {
  return StyleSheet.create({
    badgeButton: {
      alignSelf: 'flex-start',
    },
    placeholderBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: 4,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.08),
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderStyle: 'dashed',
      gap: 4,
    },
    placeholderText: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.surface,
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    buttonText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      fontWeight: utils.typography.weights.medium,
    },
    pressed: {
      opacity: 0.7,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? utils.spacing.md : utils.spacing.xl,
    },
    modalContent: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      width: '100%',
      maxWidth: isMobile ? '100%' : 400,
      maxHeight: '80%',
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        } as any,
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 16,
      elevation: 4,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    modalTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    closeButton: {
      padding: utils.spacing.xs,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    optionsList: {
      maxHeight: 400,
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: addColorOpacity(tokens.color.border, 0.5),
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    optionSelected: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
    },
    optionPressed: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    optionContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.md,
    },
    optionIconContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      width: 28,
    },
    optionLabel: {
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    optionLabelSelected: {
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.primary,
    },
  });
}

